import React from 'react';
import { Props as AlertProps } from '@jbhi-fi/lanyard-ui/components/Alert/Alert';
import { JBAlert, JBTypography } from '@jbhi-fi/lanyard-ui';
import { AuthError } from './authError/AuthError';
import { pageThemeEnum } from '../../hooks/useVerifyLayout/helpers';
import { handleLoginCreate } from '../../../../../utils/signInOutUp/AuthenticationHelper';
import { SupportRequestAlert } from './supportError/SupportRequestAlert';
import { PerksWelcomeAlert } from './perksWelcome/PerksWelcome';

export interface IPossibleAlerts extends AlertProps {
  preCheck?: (params?: any) => boolean;
}

export const POSSIBLE_ALERTS: { [name: string]: IPossibleAlerts } = {
  added: {
    type: 'success',
    heading: 'All done! Your mobile number has been added to your account.',
  },
  updated: { type: 'success', heading: 'All done! Your mobile number has been updated.' },
  deleted: {
    type: 'success',
    heading: 'All done! Your mobile number has been removed from your account.',
  },
  detailsChanged: { type: 'success', heading: 'All done! Your details have been updated.' },
  googleLinked: {
    type: 'success',
    heading: 'All done! Your Google account has been linked.',
    preCheck: () => Boolean(window.Shopify.Customer.googleEmail),
  },
  googleUnlinked: {
    type: 'success',
    heading: 'All done! Your Google account has been unlinked.',
    preCheck: () => !Boolean(window.Shopify.Customer.googleEmail),
  },
  facebookLinked: {
    type: 'success',
    heading: 'All done! Your Facebook account has been linked.',
    preCheck: () => Boolean(window.Shopify.Customer.facebookEmail),
  },
  facebookUnlinked: {
    type: 'success',
    heading: 'All done! Your Facebook account has been unlinked',
    preCheck: () => !Boolean(window.Shopify.Customer.facebookEmail),
  },
  addressSaved: { type: 'success', heading: 'All done! Your addresses have been updated.' },
  addressSavedError: {
    type: 'warning',
    heading: 'Sorry, your addresses could not be updated.',
    content: 'The changes you made to your address may not have been applied.',
  },
  birthdayAddedError: {
    type: 'warning',
    heading: 'Sorry, your birthday could not be updated.',
    content: 'Please try again.',
  },
  passwordSet: { type: 'success', heading: 'All done! Your password has been set.' },
  passwordUpdated: { type: 'success', heading: 'All done! Your password has been updated.' },
  mobileVerified: {
    type: 'success',
    showIcon: true,
    heading: 'Nice! Your mobile number has been added to your account.',
    content:
      'Any in-store receipts sent to your mobile number will now automatically show in your purchase history. You can change your number anytime in the ‘Manage account’ section through the account menu above.',
  },
  AAD_Custom_external_account_linked: {
    showIcon: true,
    type: 'warning',
    heading: 'Social account already linked',
    content: 'Your social account is already linked to a different JB account.',
  },
  AAD_Custom_local_account_linked: {
    showIcon: true,
    type: 'warning',
    heading: 'Social account already linked',
    content:
      'Your JB account is already linked to another social account. Please log in using your original social account.',
  },
  AAD_Custom_local_account_not_linked: {
    showIcon: true,
    type: 'error',
    heading: "Sorry something's gone wrong.",
    children: (
      <JBTypography type="p" margin="0 0 16px 0">
        We&apos;re having trouble unlinking that social account right now, please{' '}
        <a
          href="https://support.jbhifi.com.au/hc/en-au/requests/new?ticket_form_id=360006301073"
          target="_blank"
          rel="noreferrer"
        >
          submit a request
        </a>{' '}
        and our team will be touch.
      </JBTypography>
    ),
  },
  perksWelcomeNoBirthday: (
    <PerksWelcomeAlert
      heading={`Welcome ${window.Shopify?.Customer?.firstName}, you're now a JB Perks member!`}
      content="Any in-store receipts sent to your mobile will be automatically saved in your Purchases. Your ready-to-use welcome coupon has been emailed. Coupon valid for 28 days from issue."
      isCouponBlockShown
    />
  ),
  perksWelcomeWithBirthday: (
    <PerksWelcomeAlert
      heading={`Welcome ${window.Shopify?.Customer?.firstName}, you're now a JB Perks member!`}
      content="Any in-store receipts sent to your mobile will be automatically saved in your Purchases. Your ready-to-use welcome coupon has been emailed. Coupon valid for 28 days from issue."
      isCouponBlockShown
      isBirthdayBlockShown
    />
  ),
  perksWelcomeBack: (
    <PerksWelcomeAlert
      heading={`Welcome back to JB Perks ${window.Shopify?.Customer?.firstName}!`}
      content="Your mobile number has been linked to your account. Any in-store receipts sent to your mobile will be automatically saved in your Purchases."
    />
  ),
  perksWelcomeBackWithBirthday: (
    <PerksWelcomeAlert
      heading={`Welcome back ${window.Shopify?.Customer?.firstName}! Your birthday month has been saved.`}
      content="We'll swing a special treat your way when it's your birthday."
      isCouponBlockShown={false}
      isBirthdayBlockShown
      isBirthdayIcon
    />
  ),
  perksWelcomeBackWithBirthdayAlreadySaved: (
    <>
      <JBAlert
        type="success"
        heading="Your birthday month is already saved"
        content="Looks like you've already added your birthday month. We'll swing a special treat your way when it's your birthday."
        showIcon
        closable
      />
      <PerksWelcomeAlert
        heading={`Welcome back to JB Perks ${window.Shopify?.Customer?.firstName}!`}
        content="Your mobile number has been linked to your account. Any in-store receipts sent to your mobile will be automatically saved in your Purchases."
      />
    </>
  ),
  perksAlreadyMember: {
    showIcon: true,
    type: 'success',
    heading: 'Good news! Looks like you’re already a JB Perks member.',
    content: 'Keep an eye on your inbox for your JB Perk Alerts.',
  },
  perksBirthdayAdded: (
    <PerksWelcomeAlert
      heading="Hooray! Your birthday month has been saved."
      content="We'll swing a special treat your way when it's your birthday"
      isBirthdayBlockShown
      isCouponBlockShown={false}
      isBirthdayIcon
    />
  ),
  perksBirthdayAlreadyAdded: {
    showIcon: true,
    type: 'success',
    heading: 'Your birthday month is already saved',
    content:
      "Looks like you've already added your birthday month. We'll swing a special treat your way when it's your birthday.",
  },
  perksUnsubscribed: {
    showIcon: true,
    type: 'success',
    heading: "All done! You've been unsubscribed from JB Perks",
    content:
      'You will no longer receive any JB Perks related communications but you can still redeem any unused coupons until the specified expiry date.',
  },
  perksAuthError: (
    <AuthError
      handleLogin={async () => {
        await fetch('/account/logout');
        handleLoginCreate(`/account/login?pageTheme=${pageThemeEnum.perks}`, { pageTheme: pageThemeEnum.perks });
      }}
    />
  ),
  phoneVerifiedTooManyTimes: (
    <SupportRequestAlert
      heading="Looks like this mobile number has been verified against too many accounts"
      content="We could not join you up to JB Perks at this time. So we can assist, please"
      inlineSupportLink
      supportRequestLabel="submit a request"
      supportRequestId="360006301073"
      supportRequestPrefilledEnquiryType="jb_perks_membership"
      supportRequestPrefilledSubject="JB Perks subscription error: mobile number verified against too many accounts"
    />
  ),
};
